import { useState } from 'react'
import { IconAlertTriangle, IconInfoCircle, IconLock, IconTrash, IconUsers } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate } from 'react-router-dom'

import { projectApi } from '@/api/endpoints'
import { useProject } from '@/hooks/usePermissions'
import { errorMessage, problem } from '@/api/client'
import type { Visibility } from '@/api/types'
import { tr } from '@/i18n/tr'
import { Button, RadioCard, TextField, Textarea, modals, notifications } from '@/ui'
import './settings.css'

/** Görünürlük değişiminin keseceği erişimler (sunucunun 409 yanıtından). */
interface Uyari {
  detail: string
  members: Array<{ userId: string; username: string; displayName: string }>
  teams: string[]
}

/**
 * Proje ayarları — yalnızca sahibine.
 *
 * <p>Sekme çubuğu bu sekmeyi zaten yalnızca sahibe gösteriyor, ama adres
 * çubuğundan girilebiliyordu: düzenleyici/görüntüleyici görünürlük
 * seçeneklerini ve "Projeyi sil" düğmesini görüyor, bastığında 403
 * alıyordu. Yetkisiz bir ekran gizlenerek değil, açılmayarak korunur.
 */
export function ProjectSettingsTab() {
  const { project, can } = useProject()
  const canSettings = can('project.settings')
  const canDelete = can('project.delete')
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description ?? '')
  const [uyari, setUyari] = useState<Uyari | null>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['project', project.id] })
    qc.invalidateQueries({ queryKey: ['projects'] })
  }

  const save = useMutation({
    mutationFn: () =>
      projectApi.update(project.id, { name, description, version: project.version }),
    onSuccess: () => {
      invalidate()
      notifications.show({ color: 'green', message: 'Proje güncellendi.' })
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  /**
   * Görünürlük değişimi.
   *
   * <p>SHARED → PRIVATE üyelerin erişimini keseceği için backend önce 409 ve
   * etkilenen kullanıcı listesi döner. Kimlerin erişimini kaybedeceğini
   * göstermeden onaylatmak, sessiz yetki iptali olurdu; uyarı seçimin hemen
   * altında, kişileri ve ekipleri sayarak açılır.
   */
  const changeVisibility = useMutation({
    mutationFn: ({ visibility, force }: { visibility: Visibility; force: boolean }) =>
      projectApi.setVisibility(project.id, visibility, force),
    onSuccess: () => {
      setUyari(null)
      invalidate()
      qc.invalidateQueries({ queryKey: ['members', project.id] })
      qc.invalidateQueries({ queryKey: ['project-teams', project.id] })
      notifications.show({ color: 'green', message: 'Görünürlük güncellendi.' })
    },
    onError: (err) => {
      const pd = problem(err)
      if (pd?.code === 'VISIBILITY_CHANGE_REMOVES_MEMBERS') {
        setUyari({ detail: pd.detail ?? '', members: pd.affectedMembers ?? [], teams: pd.affectedTeams ?? [] })
        return
      }
      notifications.show({ color: 'red', message: errorMessage(err) })
    },
  })

  const remove = useMutation({
    mutationFn: () => projectApi.remove(project.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projeler')
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  // Kancalardan SONRA: erken dönüş hook sırasını bozmamalı.
  if (!canSettings && !canDelete) return <Navigate to={`/projeler/${project.id}`} replace />

  const degisti = name !== project.name || description !== (project.description ?? '')
  // Sınır girdiyi kesmek yerine görünür hata: kesilen ad sessizce kaydedilirdi.
  const adHatasi = !name.trim() ? tr.common.required : name.length > 200 ? 'En fazla 200 karakter olabilir.' : undefined
  const aciklamaHatasi = description.length > 2000 ? 'En fazla 2000 karakter olabilir.' : undefined
  const sec = (v: Visibility) => {
    if (v === project.visibility || changeVisibility.isPending) return
    changeVisibility.mutate({ visibility: v, force: false })
  }

  return (
    <div className="nx-set">
      <nav className="nx-set-nav" aria-label="Ayar bölümleri">
        {canSettings && <a href="#genel">Genel</a>}
        {canSettings && <a href="#gorunurluk">{tr.project.visibility}</a>}
        {canDelete && <a href="#tehlike" className="nx-set-nav__danger">Tehlikeli bölge</a>}
      </nav>

      <div className="nx-set-body">
        {canSettings && <>
        <section id="genel" aria-labelledby="set-g" className="nx-set-card nx-set-card--flush">
          <form onSubmit={(e) => { e.preventDefault(); if (degisti && !adHatasi && !aciklamaHatasi) save.mutate() }}>
            <div className="nx-set-card__pad">
              <div className="nx-set-card__head">
                <h2 id="set-g">Genel</h2>
                <p>Proje adı ve açıklaması tüm üyelere görünür.</p>
              </div>
              <TextField label={tr.project.name} size="lg" value={name}
                error={adHatasi}
                onChange={(e) => setName(e.currentTarget.value)} />
              <Textarea label={tr.project.description} placeholder="Bu proje ne hakkında?" value={description}
                autosize minRows={3} error={aciklamaHatasi} onChange={(e) => setDescription(e.currentTarget.value)} />
            </div>
            <div className="nx-set-card__foot">
              <span>{degisti ? 'Kaydedilmemiş değişiklik var' : 'Tüm değişiklikler kaydedildi'}</span>
              <Button type="submit" loading={save.isPending} disabled={!degisti || !!adHatasi || !!aciklamaHatasi}>{tr.common.save}</Button>
            </div>
          </form>
        </section>

        <section id="gorunurluk" aria-labelledby="set-v" className="nx-set-card">
          <div className="nx-set-card__head">
            <h2 id="set-v">{tr.project.visibility}</h2>
            <p>Projeyi kimlerin görebileceğini belirler.</p>
          </div>
          <div role="radiogroup" aria-labelledby="set-v" className="nx-set-vis">
            <RadioCard checked={project.visibility === 'PRIVATE'} onSelect={() => sec('PRIVATE')}
              icon={<IconLock size={17} stroke={1.8} />} title={tr.project.private} description={tr.project.privateHint} />
            <RadioCard checked={project.visibility === 'SHARED'} onSelect={() => sec('SHARED')}
              icon={<IconUsers size={17} stroke={1.8} />} title={tr.project.shared} description={tr.project.sharedHint} />
          </div>

          {uyari && (
            <div role="alertdialog" aria-labelledby="set-w" aria-describedby="set-w-d" className="nx-set-warn">
              <span className="nx-set-warn__head">
                <span className="nx-set-warn__icon"><IconAlertTriangle size={18} stroke={1.8} aria-hidden="true" /></span>
                <strong id="set-w">Erişim kaldırılacak</strong>
              </span>
              <span id="set-w-d" className="nx-set-warn__text">
                {uyari.detail || `Proje “${tr.project.private}” yapılırsa aşağıdaki kişiler ve ekipler projeye erişimini kaybedecek.`}
              </span>
              <ul className="nx-set-warn__list">
                {uyari.members.map((m) => <li key={m.userId}>{m.displayName} (@{m.username})</li>)}
                {uyari.teams.map((t) => <li key={t}>{t} ekibi</li>)}
              </ul>
              <div className="nx-set-warn__actions">
                <Button variant="secondary" onClick={() => setUyari(null)}>{tr.common.cancel}</Button>
                <Button variant="danger" loading={changeVisibility.isPending}
                  onClick={() => changeVisibility.mutate({ visibility: 'PRIVATE', force: true })}>
                  Yine de devam et
                </Button>
              </div>
            </div>
          )}

          {project.visibility === 'PRIVATE' && (
            <div className="nx-set-note">
              <IconInfoCircle size={18} stroke={1.8} aria-hidden="true" />
              <span>
                Bu proje yalnızca size görünüyor. Başkalarıyla çalışmak için görünürlüğü “{tr.project.shared}” yapın,
                ardından <strong>{tr.project.members}</strong> sekmesinden kişi ekleyin.
              </span>
            </div>
          )}
        </section>

        </>}

        {/* Tehlikeli bölge: kenarlığı kırmızı, geri kalan her şey sakin. Renk
            burada uyarıdır; sayfanın başka hiçbir yerinde kırmızı yok. */}
        {canDelete && <section id="tehlike" aria-labelledby="set-d" className="nx-set-card nx-set-card--danger">
          <h2 id="set-d" className="nx-set-danger-title"><IconAlertTriangle size={18} stroke={1.8} aria-hidden="true" />Tehlikeli bölge</h2>
          <div className="nx-set-danger">
            <span className="nx-set-danger__text">
              <strong>Projeyi sil</strong>
              <span>Çöp kutusuna taşınır; notlar ve dosyalar korunur.</span>
            </span>
            <Button variant="danger-soft" leftIcon={<IconTrash size={16} stroke={1.8} aria-hidden="true" />}
              onClick={() => modals.openConfirmModal({
                title: `"${project.name}" silinsin mi?`,
                children: tr.project.deleteConfirm,
                labels: { confirm: tr.common.delete, cancel: tr.common.cancel },
                confirmProps: { color: 'red' },
                onConfirm: () => remove.mutate(),
              })}>
              {tr.common.delete}
            </Button>
          </div>
        </section>}
      </div>
    </div>
  )
}
